import { compact, map } from 'lodash';
import { epoch, formatEpochAsDate, formatEpochAsTime, utc } from '../../util/datetime';
import { sanitizeHtml, toUUID } from '../../util/transform';
import { DocType } from '../global/Doc.enum';
import Root from '../Root';
import type { IAccount } from './Account.interface';
import AccountAccreditation from './AccountAccreditation';
import AccountExchange from './AccountExchange';
import AccountGatewayAccreditation from './AccountGatewayAccreditation';
import AccountIntegrations from './AccountIntegrations';
import AccountLenderAccreditation from './AccountLenderAccreditation';
import AccountNotifications from './AccountNotifications';
import AccountUser from './AccountUser';
import AccountUserInterface from './AccountUserInterface';

export default class Account extends Root implements IAccount {
  idOrganizations: Array<string>;
  idOrganizationsPartner: Array<string>;
  idCurrentOrganization: string;
  idDefaultOrganization: string;
  authentication: { ts: string | number, twoFactorEnabled: boolean, twoFactorPassed: boolean };
  user: AccountUser;
  accreditations: Array<AccountAccreditation>;
  lenderAccreditations: Array<AccountLenderAccreditation>;
  gatewayAccreditations: Array<AccountGatewayAccreditation>;
  exchange: AccountExchange;
  userInterface: AccountUserInterface;
  integrations: AccountIntegrations;
  notifications: AccountNotifications;
  lastLogOn: number;
  lastLogOnPartner: number;
  internalReferenceId: string;
  externalReferenceId: string;
  isBillable: boolean;
  isDisabled: boolean;

  getAvatar() {
    return this.user.avatar;
  }

  getName() {
    return `${this.user.firstName} ${this.user.lastName}`;
  }

  getNameExchanged(idOrganization) {
    return (
      this.exchange?.alias[idOrganization]?.from ||
      this.exchange?.eMail?.from ||
      `${this.user.firstName} ${this.user.lastName}`
    );
  }

  getLenderAccreditationsNumber() {
    return this.lenderAccreditations ? this.lenderAccreditations.length : 0;
  }

  getAccreditationsNumber() {
    return this.accreditations ? this.accreditations.length : 0;
  }

  getExternalReferenceId() {
    return this.externalReferenceId;
  }

  getInternalReferenceId() {
    return this.internalReferenceId;
  }

  getEmail() {
    return this.user.eMail;
  }

  getEmailExchanged(idOrganization) {
    return (
      this.exchange?.alias[idOrganization]?.replyTo ||
      this.exchange?.eMail?.replyTo ||
      this.user.eMail
    );
  }

  getSignature() {
    return sanitizeHtml(this.exchange?.eMail?.signature);
  }

  getSignatureExchanged(idOrganization) {
    return sanitizeHtml(
      this.exchange?.alias[idOrganization]?.signature || this.exchange?.eMail?.signature || ''
    );
  }

  getPhone() {
    return ''.concat(
      this.user.prefix ? this.user.prefix.trim() : '',
      this.user.phone ? this.user.phone.trim() : ''
    );
  }

  getFormattedAddress() {
    return (this.user && this.user.address && this.user.address.formatted) || '';
  }

  isBankStatementsEnabled() {
    return (
      this.integrations &&
      this.integrations.bankStatements &&
      this.integrations.bankStatements.enabled === true &&
      this.integrations.bankStatements.code !== null
    );
  }

  isMogoEnabled() {
    return (
      this.integrations && this.integrations.mogo && this.integrations.mogo.enabled === true
    );
  }

  isCashDeckEnabled() {
    return (
      this.integrations &&
      this.integrations.cashDeck &&
      this.integrations.cashDeck.enabled === true &&
      this.integrations.cashDeck.apiKey !== null
    );
  }

  isDropboxEnabled() {
    return (
      this.integrations &&
      this.integrations.dropbox &&
      this.integrations.dropbox.enabled === true &&
      this.integrations.dropbox.token !== null
    );
  }

  isGoogleDriveEnabled() {
    return (
      this.integrations &&
      this.integrations.googleDrive &&
      this.integrations.googleDrive.enabled === true
    );
  }

  isGoogleMailEnabled() {
    return (
      this.integrations &&
      this.integrations.googleMail &&
      this.integrations.googleMail.enabled === true
    );
  }

  isGoogleContactsEnabled() {
    return (
      this.integrations &&
      this.integrations.googleContacts &&
      this.integrations.googleContacts.enabled === true
    );
  }

  isGoogleTasksEnabled() {
    return (
      this.integrations &&
      this.integrations.googleTasks &&
      this.integrations.googleTasks.enabled === true
    );
  }

  isOneDriveEnabled() {
    return (
      this.integrations &&
      this.integrations.oneDrive &&
      this.integrations.oneDrive.enabled === true
    );
  }

  isOutlookMailEnabled() {
    return (
      this.integrations &&
      this.integrations.outlookMail &&
      this.integrations.outlookMail.enabled === true
    );
  }

  isOutlookContactsEnabled() {
    return (
      this.integrations &&
      this.integrations.outlookContacts &&
      this.integrations.outlookContacts.enabled === true
    );
  }

  isOutlookTasksEnabled() {
    return (
      this.integrations &&
      this.integrations.outlookTasks &&
      this.integrations.outlookTasks.enabled === true
    );
  }

  isEquifaxEnabled() {
    return (
      this.integrations &&
      this.integrations.equifax &&
      this.integrations.equifax.enabled === true &&
      this.integrations.equifax.username &&
      this.integrations.equifax.password
    );
  }

  isIllionEnabled() {
    return (
      this.integrations &&
      this.integrations.illion &&
      this.integrations.illion.enabled === true &&
      this.integrations.illion.userKey &&
      this.integrations.illion.password &&
      this.integrations.illion.subscriberId
    );
  }

  isSlackEnabled() {
    return (
      this.integrations &&
      this.integrations.slack &&
      this.integrations.slack.enabled === true &&
      this.integrations.slack.token !== null
    );
  }

  isRpDataEnabled() {
    return (
      this.integrations &&
      this.integrations.rpData &&
      this.integrations.rpData.enabled === true &&
      this.integrations.rpData.token !== null
    );
  }

  isPriceFinderEnabled() {
    return (
      this.integrations &&
      this.integrations.priceFinder &&
      this.integrations.priceFinder.enabled === true &&
      this.integrations.priceFinder.token !== null
    );
  }

  isMercuryEnabled() {
    return (
      this.integrations &&
      this.integrations.mercury &&
      this.integrations.mercury.enabled === true &&
      this.integrations.mercury.token !== null &&
      this.integrations.mercury.apiKey !== null
    );
  }

  isMyConnectEnabled() {
    return (
      this.integrations &&
      this.integrations.myConnect &&
      this.integrations.myConnect.enabled === true &&
      this.integrations.myConnect.agentId !== null
    );
  }

  isMovingHubEnabled() {
    return (
      this.integrations &&
      this.integrations.movingHub &&
      this.integrations.movingHub.enabled === true &&
      this.integrations.movingHub.agentId !== null
    );
  }

  isAliEnabled() {
    return (
      this.integrations &&
      this.integrations.ali &&
      this.integrations.ali.enabled === true &&
      this.integrations.ali.brokerId !== null &&
      this.integrations.ali.brokerEmail !== null
    );
  }

  isAllianzEnabled() {
    return (
      this.integrations?.allianz.enabled &&
      this.integrations?.allianz?.placingOfficerCode !== ''
    );
  }

  isCreditFixSolutionEnabled() {
    return this.integrations?.creditFixSolution?.enabled;
  }

  isHoneyEnabled() {
    return this.integrations?.honey.enabled && this.integrations?.honey?.brokerId !== '';
  }

  isFlexEnabled() {
    return (
      this.integrations &&
      this.integrations.flex &&
      this.integrations.flex.enabled === true &&
      this.integrations.flex.username !== null &&
      this.integrations.flex.password !== null
    );
  }

  isPepperResolveEnabled() {
    return (
      this.integrations &&
      this.integrations.pepperResolve &&
      this.integrations.pepperResolve.enabled === true &&
      this.integrations.pepperResolve.brokerId
    );
  }

  isVeroEnabled() {
    return this.integrations?.vero?.enabled && this.integrations?.vero?.brokerIdNumber;
  }

  isLidEnabled() {
    return this.integrations?.lid?.enabled;
  }

  isCompareClubEnabled() {
    return this.integrations?.compareClub?.enabled;
  }

  isSettleEasyEnabled() {
    return this.integrations?.settleEasy?.enabled;
  }

  isOnboarded() {
    return this.hasAccountDetails() && this.hasLastLogOn();
  }

  hasAccountDetails() {
    return (
      this.user.firstName !== '' &&
      this.user.lastName !== '' &&
      this.user.prefix !== '' &&
      this.user.phone !== '' &&
      this.user.address &&
      this.user.address.street &&
      this.user.address.street !== '' &&
      this.user.location !== ''
    );
  }

  hasLastLogOn() {
    return this.lastLogOn !== '';
  }

  isPasswordChangeRequired(duration) {
    if (duration > 0 && (!this.authentication || !this.authentication.ts)) {
      return true;
    }
    return (
      duration > 0 && utc(this.authentication.ts).diffNow('days').toObject().days > duration
    );
  }

  getDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.timeStamp, timeZone, locale, localeFormat);
  }

  getTime(timeZone, locale, localeFormat) {
    return formatEpochAsTime(this.timeStamp, timeZone, locale, localeFormat);
  }

  static build = (data: IAccount) => new Account(data);

  static blank = () => new Account({ isActive: true });

  /**
   * Constructor
   * @param {IAccount} data
   */
  constructor(data?: IAccount) {
    super(DocType.of('__global.account'), data);
    this.idOrganizations = compact(map(data?.idOrganizations, (obj) => toUUID(obj)));
    this.idOrganizationsPartner = compact(
      map(data?.idOrganizationsPartner, (obj) => toUUID(obj))
    );
    this.idCurrentOrganization = toUUID(data?.idCurrentOrganization);
    this.idDefaultOrganization = toUUID(data?.idDefaultOrganization);
    this.authentication = data?.authentication || {};
    this.user = new AccountUser(data?.user);
    this.accreditations = map(data?.accreditations, (obj) => new AccountAccreditation(obj));
    this.lenderAccreditations = map(
      data?.lenderAccreditations,
      (obj) => new AccountLenderAccreditation(obj)
    );
    this.gatewayAccreditations = map(
      data?.gatewayAccreditations,
      (obj) => new AccountGatewayAccreditation(obj)
    );
    this.exchange = new AccountExchange(data?.exchange);
    this.userInterface = new AccountUserInterface(data?.userInterface);
    this.integrations = new AccountIntegrations(data?.integrations);
    this.notifications = new AccountNotifications(data?.notifications);
    this.timeStamp = epoch(utc(data?.timeStamp));
    this.lastLogOn = epoch(utc(data?.lastLogOn));
    this.lastLogOnPartner = data?.lastLogOnPartner
      ? epoch(utc(data?.lastLogOnPartner))
      : undefined;
    this.internalReferenceId = data?.internalReferenceId || undefined;
    this.externalReferenceId = data?.externalReferenceId || undefined;
    this.isBillable = data?.isBillable === true;
    this.isDisabled = data?.isDisabled === true;
  }
}
